/**
 * InReviewSettingsPanel — hauler-configurable InReview settings
 * (Phase 11 / Task 11.1, DEC-120). Only MANAGE_TENANT holders (TENANT_ADMIN, PLATFORM_ADMIN —
 * `canEdit`) can change it, which is the API's own PATCH gate. Quick 261013-srg decision 3:
 * dispatch managers VIEW it read-only (GET is MANAGE_REFERENCE_DATA), with a line saying who
 * can change it — the same pattern as CompletionPolicyPanel.
 *
 * A labelled + described toggle per flag, the three number inputs (volume anomaly
 * threshold, stale-escalation hours, late-record hours — DEC-352), and a Save button. Changes take effect on
 * the next completed job only — existing InReview tickets are not re-evaluated.
 */

import * as React from 'react';
import type { InReviewSettings } from '@lodex-pro/shared';
import { useInReviewSettings, useUpdateInReviewSettings } from './use-inreview';

interface ToggleDef {
  readonly key: keyof InReviewSettings;
  readonly label: string;
  readonly description: string;
}

const TOGGLES: readonly ToggleDef[] = [
  {
    key: 'requireInreviewForAllTickets',
    label: 'Require review for ALL tickets',
    description:
      'When on, every completed ticket enters review regardless of flags. When off, only flagged tickets are reviewed.',
  },
  {
    key: 'flagGpsOutsideRange',
    label: 'Flag GPS outside range',
    description: "Flag tickets where the driver's GPS was outside the expected site boundary.",
  },
  {
    key: 'flagGpsDisposalJobTypesOnly',
    label: 'GPS flag for disposal jobs only',
    description: 'Only apply the GPS flag when the job ends at a disposal well.',
  },
  {
    // DEC-319 (d) — off by default. Turn it on once your tank batteries are placed
    // (a pin, or a linked well): before that, most pickups cannot be checked.
    key: 'flagGpsUnverifiable',
    label: 'Flag pickups GPS could not check',
    description:
      'Flag tickets where the driver arrived at a pickup that has no location (no pin and no linked well), so the arrival could not be GPS-checked. Turn this on once your tank batteries are placed.',
  },
  {
    key: 'flagDispatcherCompleted',
    label: 'Flag dispatcher-completed tickets',
    description:
      'Flag tickets that were completed in the office (a dispatcher, manager or admin) rather than by the driver.',
  },
  {
    key: 'flagVolumeAnomaly',
    label: 'Flag volume anomaly',
    description: 'Flag tickets where the hauled volume exceeds the threshold below.',
  },
  {
    key: 'flagMissingGauge',
    label: 'Flag missing gauge reading',
    description: 'Flag tickets where no gauge reading was recorded.',
  },
  {
    key: 'flagMissingSignature',
    label: 'Flag unsigned drivers',
    // DEC-224 — drivers now save one signature that prints on every ticket, so the
    // reviewable condition is a driver who never saved one, not a missing per-ticket
    // capture.
    description:
      'Flag tickets whose driver has not saved a signature, so the ticket printed a blank signature line.',
  },
  {
    // OPEN-121 — only REQUIRED-photo job types can raise it.
    key: 'flagMissingPhoto',
    label: 'Flag missing load photos',
    description:
      'Flag tickets on job types that require a photo when the driver took none, skipped it with a reason, or used an older app that does not report photos.',
  },
  {
    // Firestone gap 10 — the QuickBooks Class comes from the truck.
    key: 'flagMissingTruck',
    label: 'Flag tickets with no truck',
    description:
      'Flag tickets where no truck was recorded, so they would export to QuickBooks with no Class.',
  },
  {
    // OPEN-217 / DEC-382 — driver data only; office edits never raise it.
    key: 'flagValidationRule',
    label: 'Flag tickets that break a driver entry rule',
    description:
      'Flag tickets where a driver entry broke one of your Driver entry rules and was not settled on the phone: a Block not fixed, a Warning not confirmed, or a rule the phone did not have yet.',
  },
  {
    key: 'flagDuplicateTimestamps',
    label: 'Flag duplicate timestamps',
    description: 'Flag tickets where two events were recorded at the same time.',
  },
  {
    // #60 / DEC-365 (D8) — the Site Changed flag is always set; this decides whether it alone
    // sends a ticket to review. Default on.
    key: 'routeSiteChangeToReview',
    label: 'Send tickets to review when the driver changed the site',
    description:
      'When on, a ticket whose pickup site was changed (by the driver or the office) goes to review, even if nothing else is flagged. When off, it can go straight to billing; the Site Changed flag still shows if it is reviewed for another reason.',
  },
];

function Toggle({
  checked,
  onChange,
  labelId,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  labelId: string;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      disabled={disabled}
      onClick={(): void => {
        onChange(!checked);
      }}
      style={{
        position: 'relative',
        width: '42px',
        height: '24px',
        flexShrink: 0,
        borderRadius: '12px',
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        backgroundColor: checked ? 'var(--color-accent-blue)' : 'var(--color-border-primary)',
        transition: 'background-color 120ms',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '3px',
          left: checked ? '21px' : '3px',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          backgroundColor: 'var(--color-text-primary)',
          transition: 'left 120ms',
        }}
      />
    </button>
  );
}

const numberInputStyle: React.CSSProperties = {
  height: '36px',
  width: '120px',
  padding: '0 10px',
  fontSize: '14px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};

export function InReviewSettingsPanel({
  canEdit,
}: {
  readonly canEdit: boolean;
}): React.JSX.Element {
  const { data, isLoading, isError } = useInReviewSettings(true);
  const mutation = useUpdateInReviewSettings();

  const [form, setForm] = React.useState<InReviewSettings | null>(null);

  React.useEffect(() => {
    if (data?.settings !== undefined) setForm(data.settings);
  }, [data]);

  if (isLoading || form === null) {
    return (
      <p style={{ color: 'var(--color-text-tertiary)', fontSize: '14px' }}>Loading settings…</p>
    );
  }
  if (isError) {
    return (
      <p style={{ color: 'var(--color-danger)', fontSize: '14px' }}>
        Failed to load InReview settings.
      </p>
    );
  }

  const setBool = (key: keyof InReviewSettings, v: boolean): void => {
    setForm((prev) => (prev === null ? prev : { ...prev, [key]: v }));
  };
  const setNum = (key: keyof InReviewSettings, v: number): void => {
    setForm((prev) => (prev === null ? prev : { ...prev, [key]: v }));
  };

  // OPEN-169 addendum — Send Back can be turned on only once the phone has a correction flow.
  const sendBackAvailable = data?.sendBackAvailable === true;

  const onSave = (): void => {
    // While Send Back is locked the switch cannot change, so it is not sent (the API refuses
    // allowSendBack: true until the phone flow ships).
    if (sendBackAvailable) {
      mutation.mutate(form);
      return;
    }
    const { allowSendBack: _locked, ...rest } = form;
    mutation.mutate(rest);
  };

  return (
    <div style={{ maxWidth: '640px' }}>
      <h2
        style={{
          fontSize: '18px',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: '0 0 4px',
        }}
      >
        InReview Settings
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', margin: '0 0 16px' }}>
        Control which completed tickets enter the InReview queue and which conditions flag them.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {TOGGLES.map((t) => {
          const labelId = `inreview-toggle-${t.key}`;
          return (
            <div key={t.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <Toggle
                checked={form[t.key] as boolean}
                onChange={(v): void => {
                  setBool(t.key, v);
                }}
                labelId={labelId}
                disabled={!canEdit}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  id={labelId}
                  style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  {t.label}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                  {t.description}
                </div>
              </div>
            </div>
          );
        })}

        {/* OPEN-169 addendum — per-company Send Back, default off. Locked off (disabled) until
            the phone app can correct a sent-back ticket. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <Toggle
            checked={form.allowSendBack === true}
            onChange={(v): void => {
              setBool('allowSendBack', v);
            }}
            labelId="inreview-toggle-allowSendBack"
            disabled={!canEdit || !sendBackAvailable}
          />
          <div style={{ minWidth: 0 }}>
            <div
              id="inreview-toggle-allowSendBack"
              style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}
            >
              Allow Send Back to driver
            </div>
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
              When on, a reviewer can send a ticket back to its driver to correct. When off, the
              ticket is fixed in review.
            </div>
            {!sendBackAvailable && (
              <div
                data-testid="send-back-locked-note"
                style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}
              >
                Driver corrections need the phone app update.
              </div>
            )}
          </div>
        </div>

        {form.flagVolumeAnomaly && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '54px' }}>
            <label
              htmlFor="inreview-volume-threshold"
              style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}
            >
              Volume anomaly threshold (BBL)
            </label>
            <input
              id="inreview-volume-threshold"
              type="number"
              min={1}
              value={form.volumeAnomalyThresholdBbl}
              disabled={!canEdit}
              onChange={(e): void => {
                setNum('volumeAnomalyThresholdBbl', Number(e.target.value));
              }}
              style={numberInputStyle}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            htmlFor="inreview-escalation-hours"
            style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}
          >
            Auto-escalate stale tickets after (hours)
          </label>
          <input
            id="inreview-escalation-hours"
            type="number"
            min={1}
            max={336}
            value={form.escalationThresholdHours}
            disabled={!canEdit}
            onChange={(e): void => {
              setNum('escalationThresholdHours', Number(e.target.value));
            }}
            style={numberInputStyle}
          />
        </div>

        {/* DEC-352 — not a toggle: a late record is always accepted and always flagged
            (Time Check); this only sets when "late" starts. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            htmlFor="inreview-late-record-hours"
            style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}
          >
            Flag driver records that arrive late, after (hours)
          </label>
          <input
            id="inreview-late-record-hours"
            type="number"
            min={1}
            max={336}
            value={form.lateRecordThresholdHours}
            disabled={!canEdit}
            onChange={(e): void => {
              setNum('lateRecordThresholdHours', Number(e.target.value));
            }}
            style={numberInputStyle}
          />
          <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
            A record that reaches us this long after it happened (a phone that was offline) is
            accepted and its ticket gets a Time Check flag. A phone whose clock is more than 10
            minutes wrong is always flagged.
          </div>
        </div>
      </div>

      {!canEdit ? (
        <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '20px' }}>
          Only a company admin can change these.
        </p>
      ) : (
        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            type="button"
            onClick={onSave}
            disabled={mutation.isPending}
            style={{
              height: '38px',
              padding: '0 18px',
              fontSize: '14px',
              fontWeight: 600,
              borderRadius: '6px',
              border: 'none',
              cursor: mutation.isPending ? 'default' : 'pointer',
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              opacity: mutation.isPending ? 0.7 : 1,
            }}
          >
            {mutation.isPending ? 'Saving…' : 'Save settings'}
          </button>
          {mutation.isSuccess && (
            <span style={{ fontSize: '13px', color: 'var(--color-success)' }}>Saved.</span>
          )}
          {mutation.isError && (
            <span style={{ fontSize: '13px', color: 'var(--color-danger)' }}>
              Save failed — try again.
            </span>
          )}
        </div>
      )}

      <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginTop: '12px' }}>
        Changes apply to tickets completed after saving. Tickets already in review are not affected.
      </p>
    </div>
  );
}
